import React, { useCallback, useEffect, useRef, useState } from "react";
import { Canvas } from "@react-three/fiber";

import GaussianRenderer from "./GaussianRenderer";
import roomPly from "../../assets/splats/demo-room-inria-v1.ply";
import { debugLog } from "../../utils/debug";

import "./GaussianDemo.css";

const GAUSSIAN_REPRESENTATIONS = new Set(["splats", "points"]);
const SUPPORTED_FORMATS = new Set(["ply", "splat", "ksplat", "spz"]);

const DEFAULT_SCENE = {
  source: roomPly,
  name: "Synthetic Room",
  filename: "demo-room-inria-v1.ply",
  format: "PLY",
};

const GAUSSIAN_INFO = {
  splats: {
    eyebrow: "3D GAUSSIAN SPLATTING · SPLATS",
    title: "3D Gaussian Splatting",
    description:
      "A custom browser implementation of the rendering side of 3D Gaussian Splatting. Anisotropic 3D Gaussians are projected into screen-space ellipses, depth-sorted, and alpha-composited to reconstruct the scene in real time.",
    implementation: [
      "Constructs each anisotropic 3D covariance from its scale and quaternion rotation",
      "Projects 3D covariance into screen space using the perspective-projection Jacobian",
      "Eigen-decomposes the resulting 2D covariance to determine each splat's elliptical footprint",
      "Gaussian properties are packed into GPU textures shared across instanced draw batches",
      "Gaussians are globally depth-sorted back-to-front for transparency compositing",
      "The fragment shader evaluates the 2D Gaussian falloff and discards contributions below an adjustable alpha cutoff",
      "Supports PLY, SPLAT, KSPLAT, and SPZ Gaussian scene files",
      {
        text: "Implemented as a custom WebGL renderer using Three.js",
        links: [
          {
            term: "Three.js",
            url: "https://threejs.org/",
          },
        ],
      },
    ],
    pipeline: [
      "Gaussian Data",
      "3D Covariance",
      "2D Projection",
      "Depth Sort",
      "Gaussian Rasterization",
    ],
    references: [
      {
        title: "3D Gaussian Splatting for Real-Time Radiance Field Rendering",
        meta: "Kerbl et al. · SIGGRAPH 2023",
        url: "https://repo-sam.inria.fr/fungraph/3d-gaussian-splatting/",
      },
    ],
  },

  points: {
    eyebrow: "3D GAUSSIAN SPLATTING · POINTS",
    title: "Gaussian Centers",
    description:
      "A reference visualization of the same scene data rendered as conventional point primitives. Removing each Gaussian's anisotropic footprint exposes the underlying distribution of primitive centers and provides a direct comparison with splat-based reconstruction.",
    implementation: [
      "Uses the same parsed scene data as the Gaussian splat renderer",
      "Renders Gaussian centers as conventional Three.js point primitives",
      "Uses stored per-Gaussian color while omitting scale, rotation, and opacity from the rendered footprint",
      "Provides a reference view of the underlying Gaussian distribution for comparison with splat rendering",
    ],
    pipeline: [
      "Gaussian Data",
      "Primitive Centers",
      "Point Projection",
      "Point Rasterization",
      "Image",
    ],
    references: [
      {
        title: "3D Gaussian Splatting for Real-Time Radiance Field Rendering",
        meta: "Kerbl et al. · SIGGRAPH 2023",
        url: "https://repo-sam.inria.fr/fungraph/3d-gaussian-splatting/",
      },
    ],
  },
};

function parseGaussianRoute(route) {
  return GAUSSIAN_REPRESENTATIONS.has(route) ? route : "splats";
}

export default function GaussianDemo({
  Panel,
  Control,
  Slider,
  Stat,
  route,
  onNavigate,
  onInfoChange,
}) {
  const [representation, setRepresentation] = useState(() =>
    parseGaussianRoute(route),
  );

  const [sceneSource, setSceneSource] = useState(DEFAULT_SCENE.source);
  const [sceneName, setSceneName] = useState(DEFAULT_SCENE.name);
  const [sceneFilename, setSceneFilename] = useState(DEFAULT_SCENE.filename);
  const [sceneCount, setSceneCount] = useState(0);
  const [sceneFormat, setSceneFormat] = useState(DEFAULT_SCENE.format);
  const [status, setStatus] = useState("Loading...");

  const [showGrid, setShowGrid] = useState(false);
  const [splatScale, setSplatScale] = useState(1);
  const [opacity, setOpacity] = useState(1);
  const [alphaCutoff, setAlphaCutoff] = useState(0.01);
  const [resetCameraToken, setResetCameraToken] = useState(0);

  const fileInputRef = useRef(null);
  const objectURLRef = useRef(null);

  const revokeLocalScene = useCallback(() => {
    if (!objectURLRef.current) {
      return;
    }

    URL.revokeObjectURL(objectURLRef.current);
    objectURLRef.current = null;
  }, []);

  useEffect(() => revokeLocalScene, [revokeLocalScene]);

  useEffect(() => {
    setRepresentation(parseGaussianRoute(route));
  }, [route]);

  useEffect(() => {
    onInfoChange?.(GAUSSIAN_INFO[representation]);
  }, [representation, onInfoChange]);

  function navigateRepresentation(nextRepresentation) {
    setRepresentation(nextRepresentation);
    onNavigate?.(nextRepresentation);
  }

  const loadLocalFile = useCallback(
    (event) => {
      const file = event.target.files?.[0];

      if (!file) {
        return;
      }

      const extension = file.name.split(".").pop()?.toLowerCase();

      if (!SUPPORTED_FORMATS.has(extension)) {
        setStatus("Supported formats: .ply, .splat, .ksplat, .spz");
        event.target.value = "";
        return;
      }

      debugLog("[GaussianDemo] Selected file:", {
        name: file.name,
        size: file.size,
        type: file.type,
        extension,
      });

      revokeLocalScene();

      const url = URL.createObjectURL(file);
      objectURLRef.current = url;

      setSceneSource(url);
      setSceneFilename(file.name);
      setSceneName(file.name);
      setSceneCount(0);
      setSceneFormat(extension.toUpperCase());
      setStatus("Loading...");

      // Allows the same file to trigger onChange if selected again.
      event.target.value = "";
    },
    [revokeLocalScene],
  );

  const restoreDefault = useCallback(() => {
    revokeLocalScene();

    setSceneSource(DEFAULT_SCENE.source);
    setSceneFilename(DEFAULT_SCENE.filename);
    setSceneName(DEFAULT_SCENE.name);
    setSceneCount(0);
    setSceneFormat(DEFAULT_SCENE.format);
    setStatus("Loading...");
  }, [revokeLocalScene]);

  const handleSceneLoaded = useCallback((data) => {
    setSceneCount(data.count);

    if (data.format) {
      setSceneFormat(data.format);
    }
  }, []);

  return (
    <div className="reconstruction-workspace">
      <aside className="reconstruction-sidebar">
        <Panel title="REPRESENTATION">
          <div className="representation-toggle">
            <button
              type="button"
              className={representation === "splats" ? "active" : ""}
              onClick={() => navigateRepresentation("splats")}
            >
              SPLATS
            </button>

            <button
              type="button"
              className={representation === "points" ? "active" : ""}
              onClick={() => navigateRepresentation("points")}
            >
              POINTS
            </button>
          </div>
        </Panel>

        <Panel title="SCENE">
          <button
            type="button"
            className="reconstruction-action"
            onClick={() => fileInputRef.current?.click()}
          >
            LOAD SCENE
          </button>

          <input
            ref={fileInputRef}
            type="file"
            accept=".ply,.splat,.ksplat,.spz"
            hidden
            onChange={loadLocalFile}
          />

          <button
            type="button"
            className="reconstruction-action secondary"
            onClick={restoreDefault}
          >
            DEFAULT SCENE
          </button>
        </Panel>

        {representation === "splats" && (
          <Panel title="SPLAT CONTROLS">
            <Slider
              label="Scale"
              value={splatScale}
              min={0.1}
              max={3}
              step={0.05}
              onChange={setSplatScale}
              display={`${splatScale.toFixed(2)}×`}
            />

            <Slider
              label="Opacity"
              value={opacity}
              min={0}
              max={2}
              step={0.05}
              onChange={setOpacity}
              display={opacity.toFixed(2)}
            />

            <Slider
              label="Alpha Cutoff"
              value={alphaCutoff}
              min={0.001}
              max={0.1}
              step={0.001}
              onChange={setAlphaCutoff}
              display={alphaCutoff.toFixed(3)}
            />
          </Panel>
        )}

        <Panel title="VIEW">
          <Control
            label="Reference Grid"
            checked={showGrid}
            onChange={setShowGrid}
          />

          <button
            type="button"
            className="reconstruction-action secondary"
            onClick={() => setResetCameraToken((value) => value + 1)}
          >
            RESET CAMERA
          </button>
        </Panel>

        <Panel title="DATASET">
          <Stat label="Scene" value={sceneName} />
          <Stat
            label="Rendered"
            value={sceneCount ? sceneCount.toLocaleString() : "—"}
          />
          <Stat label="Format" value={sceneFormat} />
        </Panel>

        <Panel title="STATUS">
          <div className="lab-status">
            <span
              className={status === "Ready" ? "status-dot ready" : "status-dot"}
            />
            {status}
          </div>
        </Panel>
      </aside>

      <main className="reconstruction-viewer">
        <Canvas
          gl={{
            antialias: false,
            alpha: false,
          }}
          dpr={[1, 1.5]}
        >
          <GaussianRenderer
            source={sceneSource}
            filename={sceneFilename}
            representation={representation}
            splatScale={splatScale}
            opacity={opacity}
            alphaCutoff={alphaCutoff}
            showGrid={showGrid}
            resetCameraToken={resetCameraToken}
            onStatus={setStatus}
            onSceneLoaded={handleSceneLoaded}
          />
        </Canvas>

        <div className="viewer-overlay viewer-overlay-bottom">
          Drag to orbit · Scroll to zoom · Right-click to pan
        </div>
      </main>
    </div>
  );
}
